import React, { useMemo, useState } from 'react';
import {
  FiFilter as Filter,
  FiDownload as Download,
  FiClock as Clock,
  FiHash as Hash,
  FiChevronDown as ChevronDown,
  FiChevronRight as ChevronRight
} from 'react-icons/fi';

/**
 * WaterfallChartComponent - Timeline waterfall visualization for agent run traces
 *
 * Shows spans as horizontal bars on a timeline with parent-child hierarchy
 */

interface SpanColorEntry {
  bg: string;
  border: string;
  light: string;
}

const SPAN_COLORS: Record<string, SpanColorEntry> = {
  llm: { bg: 'bg-blue-500', border: 'border-blue-600', light: 'bg-blue-100 dark:bg-blue-900/30' },
  tool: { bg: 'bg-purple-500', border: 'border-purple-600', light: 'bg-purple-100 dark:bg-purple-900/30' },
  memory: { bg: 'bg-green-500', border: 'border-green-600', light: 'bg-green-100 dark:bg-green-900/30' },
  reasoning: { bg: 'bg-amber-500', border: 'border-amber-600', light: 'bg-amber-100 dark:bg-amber-900/30' },
  generation: { bg: 'bg-blue-500', border: 'border-blue-600', light: 'bg-blue-100 dark:bg-blue-900/30' },
  action: { bg: 'bg-purple-500', border: 'border-purple-600', light: 'bg-purple-100 dark:bg-purple-900/30' },
  observation: { bg: 'bg-green-500', border: 'border-green-600', light: 'bg-green-100 dark:bg-green-900/30' },
  other: { bg: 'bg-gray-500', border: 'border-gray-600', light: 'bg-gray-100 dark:bg-gray-700/30' },
};

const getSpanColorClass = (type: string | undefined): SpanColorEntry => {
  const normalizedType = type?.toLowerCase() ?? 'other';
  return SPAN_COLORS[normalizedType] ?? SPAN_COLORS.other;
};

interface StepData {
  id?: string;
  step_number?: number;
  tool_used?: string;
  action?: string;
  duration_seconds?: number;
  input_tokens?: number;
  output_tokens?: number;
  cost?: number;
  status?: string;
  input?: unknown;
  output?: unknown;
}

// Derive step type from tool_used or action
const deriveStepType = (step: StepData): string => {
  const toolUsed = step.tool_used?.toLowerCase() ?? '';
  const action = step.action?.toLowerCase() ?? '';

  if (toolUsed) {
    if (toolUsed.includes('llm') || toolUsed.includes('generate') || toolUsed.includes('chat') || toolUsed.includes('complete')) {
      return 'llm';
    }
    if (toolUsed.includes('memory') || toolUsed.includes('retrieve') || toolUsed.includes('store')) {
      return 'memory';
    }
    if (toolUsed.includes('reason') || toolUsed.includes('think') || toolUsed.includes('plan')) {
      return 'reasoning';
    }
    return 'tool';
  }

  if (action.includes('generate') || action.includes('llm') || action.includes('response')) {
    return 'llm';
  }
  if (action.includes('tool') || action.includes('execute') || action.includes('call')) {
    return 'tool';
  }
  if (action.includes('observe') || action.includes('result')) {
    return 'observation';
  }

  return 'action';
};

export interface WaterfallSpan {
  id: string;
  name: string;
  type: string;
  start: number;
  duration: number;
  end: number;
  depth: number;
  metadata: {
    step_number?: number;
    tool_used?: string | null;
    action?: string | null;
    input_tokens?: number;
    output_tokens?: number;
    cost?: number;
    status?: string;
    input?: string | unknown;
    output?: string | unknown;
  };
}

// Transform steps to waterfall format
export const transformStepsToWaterfall = (steps: StepData[], _startTime?: string): WaterfallSpan[] => {
  if (!steps || steps.length === 0) return [];

  let currentOffset = 0;

  return steps.map((step, index) => {
    const duration = (step.duration_seconds ?? 0) * 1000;
    const start = currentOffset;
    currentOffset += duration;

    const stepName = step.action ?? step.tool_used ?? `Step ${step.step_number ?? index + 1}`;
    const stepType = deriveStepType(step);

    return {
      id: step.id ?? `step-${index}`,
      name: stepName,
      type: stepType,
      start,
      duration,
      end: start + duration,
      depth: 0,
      metadata: {
        step_number: step.step_number ?? index + 1,
        tool_used: step.tool_used,
        action: step.action,
        input_tokens: step.input_tokens,
        output_tokens: step.output_tokens,
        cost: step.cost,
        status: step.status,
        input: step.input,
        output: step.output,
      },
    };
  });
};

interface TimelineBarProps {
  span: WaterfallSpan;
  totalDuration: number;
  width: number;
  barHeight: number;
  showDetails: boolean;
  onToggleDetails: (id: string) => void;
  onHover: (span: WaterfallSpan | null) => void;
  isHovered: boolean;
}

const TimelineBar: React.FC<TimelineBarProps> = ({
  span,
  totalDuration,
  showDetails,
  onToggleDetails,
  onHover,
  isHovered
}) => {
  const colors = getSpanColorClass(span.type);
  const leftPercent = totalDuration > 0 ? (span.start / totalDuration) * 100 : 0;
  const widthPercent = totalDuration > 0 ? (span.duration / totalDuration) * 100 : 0;

  return (
    <div
      className={`group relative flex items-center py-2 ${isHovered ? colors.light : ''}`}
      role="presentation"
      onMouseEnter={() => onHover(span)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Left side - Span info */}
      <div className="w-64 flex-shrink-0 flex items-center px-3">
        <button
          onClick={() => onToggleDetails(span.id)}
          className="mr-2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          {showDetails ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
        <div className={`w-2 h-2 rounded-full ${colors.bg} mr-2`} />
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={span.name}>
          {span.name}
        </span>
      </div>

      {/* Right side - Timeline bar */}
      <div className="flex-1 relative h-8 bg-gray-100 dark:bg-gray-800/50 rounded">
        <div
          className={`absolute h-6 top-1 rounded ${colors.bg} shadow-sm flex items-center justify-end pr-2 transition-all`}
          style={{
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, 0.5)}%`,
            minWidth: '4px',
          }}
        >
          {widthPercent > 8 && (
            <span className="text-xs text-white font-medium">
              {span.duration >= 1000
                ? `${(span.duration / 1000).toFixed(2)}s`
                : `${span.duration.toFixed(0)}ms`
              }
            </span>
          )}
        </div>
      </div>

      {/* Duration label */}
      <div className="w-24 flex-shrink-0 text-right pr-3">
        <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
          {span.duration >= 1000
            ? `${(span.duration / 1000).toFixed(2)}s`
            : `${span.duration.toFixed(0)}ms`
          }
        </span>
      </div>
    </div>
  );
};

interface SpanDetailsProps {
  span: WaterfallSpan;
}

const SpanDetails: React.FC<SpanDetailsProps> = ({ span }) => {
  const [activeTab, setActiveTab] = useState<'info' | 'input' | 'output'>('info');

  return (
    <div className="ml-8 mr-4 mb-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Tabs */}
      <div className="flex space-x-4 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('info')}
          className={`text-sm font-medium ${
            activeTab === 'info'
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
        >
          Info
        </button>
        {span.metadata.input != null && (
          <button
            onClick={() => setActiveTab('input')}
            className={`text-sm font-medium ${
              activeTab === 'input'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            Input
          </button>
        )}
        {span.metadata.output != null && (
          <button
            onClick={() => setActiveTab('output')}
            className={`text-sm font-medium ${
              activeTab === 'output'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            Output
          </button>
        )}
      </div>

      {/* Tab content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Type</span>
            <p className="font-medium text-gray-900 dark:text-white capitalize">{span.type}</p>
          </div>
          {span.metadata.tool_used && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Tool</span>
              <p className="font-medium text-gray-900 dark:text-white">{span.metadata.tool_used}</p>
            </div>
          )}
          <div>
            <span className="text-gray-500 dark:text-gray-400">Duration</span>
            <p className="font-medium text-gray-900 dark:text-white font-mono">
              {span.duration >= 1000
                ? `${(span.duration / 1000).toFixed(3)}s`
                : `${span.duration.toFixed(1)}ms`
              }
            </p>
          </div>
          {span.metadata.input_tokens != null && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Tokens</span>
              <p className="font-medium text-gray-900 dark:text-white font-mono">
                {span.metadata.input_tokens}&darr; / {span.metadata.output_tokens ?? 0}&uarr;
              </p>
            </div>
          )}
          {span.metadata.cost != null && span.metadata.cost > 0 && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Cost</span>
              <p className="font-medium text-gray-900 dark:text-white font-mono">
                ${Number(span.metadata.cost).toFixed(4)}
              </p>
            </div>
          )}
          <div>
            <span className="text-gray-500 dark:text-gray-400">Status</span>
            <p className={`font-medium ${
              span.metadata.status === 'completed'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}>
              {span.metadata.status ?? 'unknown'}
            </p>
          </div>
        </div>
      )}

      {activeTab === 'input' && span.metadata.input != null && (
        <div className="bg-white dark:bg-gray-900 rounded p-2 max-h-48 overflow-auto">
          <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
            {typeof span.metadata.input === 'string'
              ? span.metadata.input
              : JSON.stringify(span.metadata.input, null, 2)
            }
          </pre>
        </div>
      )}

      {activeTab === 'output' && span.metadata.output != null && (
        <div className="bg-white dark:bg-gray-900 rounded p-2 max-h-48 overflow-auto">
          <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
            {typeof span.metadata.output === 'string'
              ? span.metadata.output
              : JSON.stringify(span.metadata.output, null, 2)
            }
          </pre>
        </div>
      )}
    </div>
  );
};

interface TimeMarker {
  position: number;
  time: number;
}

interface Props {
  spans: WaterfallSpan[];
  runStartTime?: string;
  totalDuration?: number;
  onExport?: (type: string) => void;
  height?: number;
}

const WaterfallChartComponent: React.FC<Props> = ({
  spans,
  totalDuration,
  onExport,
  height = 400
}) => {
  const [expandedSpans, setExpandedSpans] = useState<Set<string>>(new Set());
  const [hoveredSpan, setHoveredSpan] = useState<WaterfallSpan | null>(null);
  const [filterTypes, setFilterTypes] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const availableTypes = useMemo<string[]>(() => {
    const types = new Set(spans.map(s => s.type?.toLowerCase() ?? 'other'));
    return Array.from(types);
  }, [spans]);

  const filteredSpans = useMemo<WaterfallSpan[]>(() => {
    if (filterTypes.size === 0) return spans;
    return spans.filter(s => filterTypes.has(s.type?.toLowerCase() ?? 'other'));
  }, [spans, filterTypes]);

  const calculatedTotalDuration = useMemo<number>(() => {
    if (totalDuration) return totalDuration * 1000;
    if (spans.length === 0) return 0;
    return Math.max(...spans.map(s => s.end ?? (s.start + s.duration)));
  }, [spans, totalDuration]);

  const toggleSpanDetails = (spanId: string): void => {
    setExpandedSpans(prev => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  };

  const toggleFilter = (type: string): void => {
    setFilterTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const clearFilters = (): void => {
    setFilterTypes(new Set());
  };

  const handleExport = (): void => {
    if (onExport) {
      onExport('waterfall');
    }
  };

  const timeMarkers = useMemo<TimeMarker[]>(() => {
    if (calculatedTotalDuration === 0) return [];
    const markers: TimeMarker[] = [];
    const interval = calculatedTotalDuration / 5;
    for (let i = 0; i <= 5; i++) {
      markers.push({
        position: (i / 5) * 100,
        time: interval * i,
      });
    }
    return markers;
  }, [calculatedTotalDuration]);

  if (!spans || spans.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-gray-500 dark:text-gray-400">No spans available for waterfall chart</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          {/* Filters */}
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                filterTypes.size > 0
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">
                {filterTypes.size > 0 ? `${filterTypes.size} filters` : 'Filter'}
              </span>
            </button>

            {showFilters && (
              <div className="absolute top-full mt-2 left-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 min-w-[200px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Span Types</span>
                  {filterTypes.size > 0 && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {availableTypes.map(type => {
                    const colors = getSpanColorClass(type);
                    return (
                      <label key={type} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterTypes.has(type)}
                          onChange={() => toggleFilter(type)}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div className={`w-2 h-2 rounded-full ${colors.bg}`} />
                        <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{type}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <span className="text-sm text-gray-600 dark:text-gray-400">
            {filteredSpans.length} of {spans.length} spans
          </span>
        </div>

        <div className="flex items-center space-x-4">
          {/* Legend */}
          <div className="flex items-center space-x-3 text-xs">
            {Object.entries(SPAN_COLORS).slice(0, 4).map(([type, colors]) => (
              <div key={type} className="flex items-center">
                <div className={`w-3 h-3 rounded ${colors.bg} mr-1`} />
                <span className="capitalize text-gray-600 dark:text-gray-400">{type}</span>
              </div>
            ))}
          </div>

          <button
            onClick={handleExport}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            title="Export"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Time scale header */}
      <div className="flex items-center border-b border-gray-200 dark:border-gray-700 pb-2 mb-2">
        <div className="w-64 flex-shrink-0 px-3">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Span</span>
        </div>
        <div className="flex-1 relative h-6">
          {timeMarkers.map((marker) => (
            <div
              key={`marker-${marker.position}-${marker.time}`}
              className="absolute text-xs text-gray-500 dark:text-gray-400"
              style={{ left: `${marker.position}%`, transform: 'translateX(-50%)' }}
            >
              {marker.time >= 1000
                ? `${(marker.time / 1000).toFixed(1)}s`
                : `${marker.time.toFixed(0)}ms`
              }
            </div>
          ))}
        </div>
        <div className="w-24 flex-shrink-0 text-right pr-3">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Duration</span>
        </div>
      </div>

      {/* Spans list */}
      <div className="overflow-auto" style={{ maxHeight: height }}>
        {filteredSpans.map(span => (
          <React.Fragment key={span.id}>
            <TimelineBar
              span={span}
              totalDuration={calculatedTotalDuration}
              width={800}
              barHeight={24}
              showDetails={expandedSpans.has(span.id)}
              onToggleDetails={toggleSpanDetails}
              onHover={setHoveredSpan}
              isHovered={hoveredSpan?.id === span.id}
            />
            {expandedSpans.has(span.id) && <SpanDetails span={span} />}
          </React.Fragment>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-6">
            <div className="flex items-center text-gray-600 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              Total: {calculatedTotalDuration >= 1000
                ? `${(calculatedTotalDuration / 1000).toFixed(2)}s`
                : `${calculatedTotalDuration.toFixed(0)}ms`
              }
            </div>
            <div className="flex items-center text-gray-600 dark:text-gray-400">
              <Hash className="w-4 h-4 mr-1" />
              {filteredSpans.length} spans
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterfallChartComponent;
